/**
 * liveTrackPick — la place de chaque piste de la salle sur la scène, pure (`liveTracks` la lit sur
 * les pistes de LiveKit) : ma caméra, la caméra du principal à sa place (`cast.camera`), celle de
 * chaque petit rond (`round:<clé>`), et le partage d'écran du principal (`cast.screens`). Quel
 * appareil va où, c'est le moteur qui le dit (`liveTutorCast`) ; si la caméra désignée n'a pas encore
 * de piste, la place prend la suivante du principal qui en a une, plutôt que sa photo. Mes autres
 * appareils (`twins`) ont chacun leur case (`twin:<identité>`) : l'écran qu'il partage, sinon sa caméra.
 */
import { type LiveTutorCast } from "../../../../../../engine/live/student/liveTutorCast"
import { type UILiveVideoFit, type UILiveVideoSource } from "../../../types/studentLive"

/** Les sources de LiveKit (`Track.Source`) que la scène montre. */
const CAMERA = "camera"
const SCREEN_SHARE = "screen_share"

/** Ce que la sélection lit d'une piste : son appareil, sa source, si elle est coupée. */
export type LiveTrackLike = {
    source: string
    identity: string | null | undefined
    isLocal: boolean
    muted: boolean
}

export function pickLiveVideoTracks<T extends LiveTrackLike>(
    tracks: readonly T[],
    cast: Pick<LiveTutorCast, "camera" | "rounds" | "screens"> & { principal?: { cameras: string[] } | null },
    twins: readonly string[] = []
): Partial<Record<UILiveVideoSource, T>> {
    const cameras = new Map<string, T>()
    const screens = new Map<string, T>()
    const result: Partial<Record<UILiveVideoSource, T>> = {}
    for (const track of tracks) {
        if (track.muted) continue
        if (track.source === CAMERA && track.isLocal) result.me = track
        else if (track.identity == null || track.isLocal) continue
        else if (track.source === CAMERA) cameras.set(track.identity, track)
        else if (track.source === SCREEN_SHARE) screens.set(track.identity, track)
    }
    const candidates = [...(cast.camera != null ? [cast.camera] : []), ...(cast.principal?.cameras ?? [])]
    const lead = candidates.find((identity) => cameras.has(identity))
    if (lead != null) result.tutor = cameras.get(lead)
    for (const round of cast.rounds) {
        // La caméra montrée en grand ne se montre pas une seconde fois dans un rond.
        if (round.camera == null || round.camera === lead) continue
        const camera = cameras.get(round.camera)
        if (camera != null) result[`round:${round.key}`] = camera
    }
    const screen = cast.screens.map((identity) => screens.get(identity)).find((track) => track != null)
    if (screen != null) result.screen = screen
    for (const twin of twins) {
        const track = screens.get(twin) ?? cameras.get(twin)
        if (track != null) result[`twin:${twin}`] = track
    }
    return result
}

/**
 * Comment une piste remplit sa place : ce que la scène demande (`fit`), sinon l'écran partagé entier et
 * une caméra rognée.
 */
export function liveVideoFit(source: UILiveVideoSource, fit?: UILiveVideoFit): UILiveVideoFit {
    return fit ?? (source === "screen" ? "contain" : "cover")
}
